import { useRef, useCallback, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import styles from './ChartTooltip.module.css'

export type TooltipVariant = 'dark' | 'light' | 'glass' | 'accent'

/**
 * 차트 툴팁. document.body에 portal로 마운트되므로 차트가 들어있는 카드의
 * `overflow: hidden`에 영향을 받지 않고 카드 경계 밖에서도 보인다.
 *
 * show(x, y, text)의 x/y는 **viewport 좌표** (event.clientX/clientY).
 */
export function useChartTooltip(variant: TooltipVariant = 'dark') {
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const show = useCallback((x: number, y: number, text: string) => {
    const el = tooltipRef.current
    if (!el) return

    el.replaceChildren()
    const lines = text.split('\n')
    lines.forEach((line, i) => {
      const colonIdx = line.indexOf(':')
      if (i > 0 && colonIdx > -1) {
        const row = document.createElement('div')
        row.className = styles.row
        const labelSpan = document.createElement('span')
        labelSpan.textContent = line.slice(0, colonIdx)
        const valueSpan = document.createElement('span')
        valueSpan.className = styles.value
        valueSpan.textContent = line.slice(colonIdx + 1).trim()
        row.append(labelSpan, valueSpan)
        el.appendChild(row)
      } else {
        const header = document.createElement('div')
        header.className = styles.header
        header.textContent = line
        el.appendChild(header)
      }
    })

    // 1차 배치 → 측정 → viewport 밖이면 반대편으로 flip / 안 들어오면 clamp
    el.style.left = `${x}px`
    el.style.top = `${y}px`
    el.style.opacity = '1'

    const rect = el.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const pad = 8

    if (rect.right > vw - pad) {
      // 오른쪽 잘림 → 커서 왼쪽으로 (호출부가 cursor+12 줬다 가정)
      const flipped = x - rect.width - 24
      el.style.left = `${Math.max(pad, flipped)}px`
    } else if (rect.left < pad) {
      el.style.left = `${pad}px`
    }

    if (rect.bottom > vh - pad) {
      const flipped = y - rect.height + 10
      el.style.top = `${Math.max(pad, flipped)}px`
    } else if (rect.top < pad) {
      el.style.top = `${pad}px`
    }
  }, [])

  const hide = useCallback(() => {
    const el = tooltipRef.current
    if (!el) return
    el.style.opacity = '0'
  }, [])

  const tooltipClassName = `${styles.base} ${styles[variant]}`

  const tooltipElement = mounted
    ? createPortal(
        <div ref={tooltipRef} className={tooltipClassName} />,
        document.body,
      )
    : null

  return { tooltipElement, show, hide }
}
